import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import { getDashboardDataService } from "@/service/dashboard.service";
import { DashboardData } from "@/types";
import type { AxiosError } from "axios";

interface DashboardState {
  dashboardData: DashboardData | null;
  loading: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  dashboardData: null,
  loading: false,
  error: null,
};

// Async Actions
export const fetchDashboardData = createAsyncThunk(
  "dashboard/fetchData",
  async (_, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const {
        data: { data },
      } = await getDashboardDataService();
      dispatch(setLoading(false));
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch dashboard data";
      dispatch(setError(message));
      throw error;
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch dashboard data
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.dashboardData = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch dashboard data";
      });
  },
});

export const { setLoading, setError, clearError, resetAllState } =
  dashboardSlice.actions;

// Selectors
export const selectDashboardData = (state: RootState) =>
  state.dashboard.dashboardData;
export const selectDashboardLoading = (state: RootState) =>
  state.dashboard.loading;
export const selectDashboardError = (state: RootState) => state.dashboard.error;

export default dashboardSlice.reducer;
